import { Checkbox, FormGroup, FormControlLabel, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import styled from 'styled-components';
import { Controller } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import masterServicesApi from '../../../apis/master-services';
import { SpaceBetweenContainer, InputField } from '../../../healtheraComponents';
import { RadioGroup, SearchAndSelect } from '../../Shared/Form';
import { Section } from '../styled-components';

const COUNTRIES = ['England', 'Wales', 'Northern Ireland', 'Scotland'];
const CONSULTATION_METHODS = [
  { label: 'Video Consultation', value: 'video_consultation' },
  { label: 'Digital Questionnaire', value: 'digital_questionnaire' },
  { label: 'Appointment Booking', value: 'appointment_booking' },
  { label: 'Walk-In', value: 'walk_in' },
];
const PAYMENT_TYPES = [
  { label: 'Free', value: 'free' },
  { label: 'Paid', value: 'paid' },
];

const useStyles = makeStyles(() => {
  return {
    formGroup: {
      marginLeft: '10px',
    },
    checkbox: {
      padding: '2px',
    },
    adornment: {
      fontSize: '18px',
    }
  }
});

function calculateUpdatedCountries(currentCountries = [], country, checked) {
  const updatedCountries = checked
    ? [...currentCountries, country]
    : currentCountries.filter(c => c !== country);

  return updatedCountries;
}

export default function ConsultationSettings({ control, watch, setValue }) {
  const classes = useStyles();

  const { data: conditions } = useQuery({
    queryKey: ['service-condition-options'],
    queryFn: () => masterServicesApi.getServiceConditions({ fields: ['_id', 'internal_name'], limit: 200 }),
  });

  const paymentType = watch('consultation.payment_type');

  useEffect(() => {
    if (paymentType === 'free') {
      setValue('consultation.price', 0);
    }
  }, [paymentType, setValue])

  const processedConditions = useMemo(() => {
    if (conditions?.data) {
      return conditions.data.map(({ _id, internal_name }) => ({ value: _id, title: internal_name }));
    }

    return [];
  }, [conditions]);

  return (
    <Section>
      <header>Consultation settings</header>
      <Box>
        <header>Countries</header>
        <Controller
          control={control}
          name="consultation.countries"
          render={({ field: { value, onChange } }) => (
            <FormGroup className={classes.formGroup}>
              {COUNTRIES.map((country) => (
                <FormControlLabel
                  key={country}
                  control={
                    <Checkbox
                      className={classes.checkbox}
                      onChange={(_, checked) => onChange(calculateUpdatedCountries(value, country, checked))}
                      checked={value?.includes(country)}
                    />
                  }
                  label={country}
                />
              ))}
            </FormGroup>
          )}
        />
      </Box>
      <SpaceBetweenContainer>
        <Box>
          <header>Local Consultation</header>
          <Controller
            control={control}
            name="consultation.local_consultation_method"
            render={({ field: { value, onChange } }) => (
              <FormGroup className={classes.formGroup}>
                {CONSULTATION_METHODS.map(({ label, value: fieldValue }) => (
                  <FormControlLabel
                    key={fieldValue}
                    control={
                      <Checkbox
                        className={classes.checkbox}
                        onChange={(_, checked) => onChange({ ...value, [fieldValue]: checked })}
                        checked={Boolean(value?.[fieldValue])}
                      />
                    }
                    label={label}
                  />
                ))}
              </FormGroup>
            )}
          />
        </Box>
        <Box>
          <header>National Consultation</header>
          <Controller
            control={control}
            name="consultation.national_consultation_method"
            render={({ field: { value, onChange } }) => (
              <FormGroup className={classes.formGroup}>
                {CONSULTATION_METHODS.map(({ label, value: fieldValue }) => (
                  <FormControlLabel
                    key={fieldValue}
                    control={
                      <Checkbox
                        className={classes.checkbox}
                        onChange={(_, checked) => onChange({ ...value, [fieldValue]: checked })}
                        checked={Boolean(value?.[fieldValue])}
                      />
                    }
                    label={label}
                  />
                ))}
              </FormGroup>
            )}
          />
        </Box>
      </SpaceBetweenContainer>
      <Box>
        <header>Consultation Payment Type</header>
        <Controller
          control={control}
          name="consultation.payment_type"
          render={({ field: { value, onChange } }) => (
            <RadioGroup name="payment_type" value={value} onChange={onChange} options={PAYMENT_TYPES} />
          )}
        />
      </Box>
      <Box>
        <header>Price</header>
        <Controller
          control={control}
          name="consultation.price"
          render={({ field: { value, onChange, ...restField } }) => (
            <InputField
              value={value}
              onChange={onChange}
              disabled={paymentType === 'free'}
              placeholder="0.00"
              InputProps={{
                startAdornment: (<InputAdornment
                  position="start"
                  disableTypography
                  className={classes.adornment}
                >£</InputAdornment>)
              }}
            />
          )}
        />
      </Box>
      <Box>
        <header>Conditions</header>
        <Controller
          control={control}
          name="consultation.conditions"
          render={({ field: { value, onChange } }) => (
            <SearchAndSelect options={processedConditions} value={value} onChange={onChange} />
          )}
        />
      </Box>
    </Section>
  );
}

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 48%;

  header {
    font-weight: 600;
    font-size: 15;
  }
`
